// A BIZONYLAT DÁTUMAINAK IGAZÍTÁSA A KIÁLLÍTÁS NAPJÁRA — tiszta függvény (nincs I/O, nincs `new Date()`).
//
// ── MIÉRT KELL ──────────────────────────────────────────────────────────────────────────────────────────
// A generátor a piszkozat keltét MINDIG a számlázási hónap 2-ára állítja (`buildInvoice`), bármelyik napon
// is fut — és ez szándékos: a kelte a NYITOTT PISZKOZATHOZ FŰZÉS hónap-megkülönböztetője (`draft-append.ts`).
// A napi generálás miatt viszont egy 20-án keletkező piszkozat is 2-ai kelttel születik, a belőle számolt
// fizetési határidővel együtt — ha ezt kiállítanánk, a bizonylat már a kiállítás pillanatában lejárt
// határidőt hordozna, a kelte pedig nem a tényleges kiállítás napja lenne.
//
// ── A SZABÁLY: AZ ELTOLÁS MEGŐRZÉSE ─────────────────────────────────────────────────────────────────────
//   eltolás      = ma − régi kelt
//   új kelt      = ma
//   új teljesítés = régi teljesítés + eltolás
//   új fiz. hat.  = régi fiz. hat.  + eltolás
//
// MIÉRT NEM ÚJRASZÁMOLJUK a teljesítést és a határidőt: nem kell tudnunk, hogy a sor előre vagy utólag
// fizetős. Előre fizetősnél a teljesítés a KELTTEL egyenlő, utólagosnál a FIZETÉSI HATÁRIDŐVEL — az eltolás
// megőrzése mindkettőt magától tartja, és túléli azt is, ha a kolléga kézzel állított be egyedi határidőt.
//
// ⚠️ A JÖVŐBELI KELTET NEM BÁNTJUK (user-döntés): ha a piszkozat kelte már a mai nap UTÁN van, azt ember
// állította be szándékosan — az eltolás ilyenkor NULLA, mind a három dátum marad.
//
// MIÉRT A KÖZÖS CSOMAGBAN: a szabályt KÉT oldal futtatja. A FAL a BFF (a kiállítás-tx ezt számolja, és amit a
// kliens küld, azt csak FELÜLÍRÁSKÉNT fogadja el), a megerősítő ablak viszont ELŐRE megmutatja a kollégának a
// kimenő dátumokat — ugyanazzal a függvénnyel. Ez a `calculateInvoiceItem` bevált mintája: egy számoló,
// szerver-oldali igazsággal, kliens-oldali előnézettel.
/**
 * Nap-pontos ISO (`YYYY-MM-DD`) vagy `null`. A busz teljes ISO DateTime-ot is adhat — a nap-részt vesszük.
 * (Az `apps/api` `dayOnly`-jával egyező szabály; itt saját, hogy a csomag függetlenül álljon.)
 */
function dayOnly(value) {
    if (typeof value !== 'string' || value === '')
        return null;
    return /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;
}
/** Nap-pontos ISO → nap-sorszám (UTC-éjfél). Csak a KÜLÖNBSÉG számít, ezért a zóna itt közömbös. */
function dayNumber(iso) {
    return (Date.UTC(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10))) /
        86_400_000);
}
/** Nap-sorszám → nap-pontos ISO. */
function fromDayNumber(n) {
    return new Date(n * 86_400_000).toISOString().slice(0, 10);
}
/** Nap-pontos ISO + N nap. */
export function shiftDay(iso, days) {
    return fromDayNumber(dayNumber(iso) + days);
}
/**
 * A kiállításkor érvényes dátum-hármas a TÁROLT piszkozat-dátumokból.
 *
 * @param stored a piszkozat fején álló három dátum (bármelyik lehet hiányzó — l. lent)
 * @param todayIso a kiállítás napja (`YYYY-MM-DD`); a hívó adja, hogy a függvény tiszta maradjon
 *
 * HIÁNYZÓ MEZŐK (degenerált eset — a generátor és a szerkesztő is mindhármat kitölti): a hiányzó keltet a MAI
 * nap pótolja, a hiányzó teljesítést az (új) kelt, a hiányzó határidőt az (új) kelt + 8 nap. A nyolc nap a
 * korábbi `todayPlus(8)` fallback-kel egyezik; a kelthez kötve viszont a jövőbeli-kelt ágon is értelmes marad.
 */
export function shiftIssueDates(stored, todayIso) {
    const regiKelt = dayOnly(stored.issueDate) ?? todayIso;
    // A JÖVŐBELI KELT érintetlen marad (szándékos emberi beállítás) — az eltolás ilyenkor nulla.
    const eltolas = regiKelt > todayIso ? 0 : dayNumber(todayIso) - dayNumber(regiKelt);
    const issueDate = shiftDay(regiKelt, eltolas);
    const regiTeljesites = dayOnly(stored.fulfillmentDate);
    const regiHatarido = dayOnly(stored.dueDate);
    return {
        issueDate,
        fulfillmentDate: regiTeljesites === null ? issueDate : shiftDay(regiTeljesites, eltolas),
        dueDate: regiHatarido === null ? shiftDay(issueDate, 8) : shiftDay(regiHatarido, eltolas),
    };
}
//# sourceMappingURL=invoice-dates.js.map